// Analyse für Strip Silence (Kap. 39) und Normalisieren. Arbeitet auf den Sample-Daten eines Clips,
// berücksichtigt Clip Gain und Clip-Loops und liefert Timeline-Zeiten.
import type { Clip } from '@srs/shared';

export interface StripOpts {
  /** Schwelle in dBFS (−96 bis 0). Darunter gilt als Stille. */
  thresholdDb: number;
  /** Mindestdauer einer Stille in ms (0 bis 4000). Kürzere Pausen bleiben im Clip, so entstehen nicht unzählige Mini-Clips. */
  minStripMs: number;
  /** Vorlauf vor jedem Clip in ms (Atmer, Anschlag) */
  padStartMs: number;
  /** Nachlauf nach jedem Clip in ms (Ausklang) */
  padEndMs: number;
}
export const DEFAULT_STRIP: StripOpts = { thresholdDb: -48, minStripMs: 250, padStartMs: 20, padEndMs: 80 };

type ClipLike = Pick<Clip, 'start' | 'offset' | 'duration' | 'gainDb' | 'loop'>;

/** Spitzenwerte je Millisekunde über die Clip-Länge (mit Clip Gain) */
export function envelopeMs(channels: Float32Array[], sr: number, c: ClipLike): Float32Array {
  const frames = Math.max(1, Math.ceil(c.duration * 1000)), out = new Float32Array(frames);
  const g = Math.pow(10, (c.gainDb ?? 0) / 20), hop = sr / 1000, len = channels[0]?.length ?? 0;
  for (let f = 0; f < frames; f++) {
    let rel = f / 1000;
    if (c.loop && c.loop.length > 0) rel %= c.loop.length;
    const i0 = Math.floor((c.offset + rel) * sr), i1 = Math.min(len, Math.floor(i0 + hop));
    let pk = 0;
    for (const ch of channels) for (let i = Math.max(0, i0); i < i1; i++) { const v = ch[i] < 0 ? -ch[i] : ch[i]; if (v > pk) pk = v; }
    out[f] = pk * g;
  }
  return out;
}

/** Bereiche mit Signal (Timeline-Zeiten), mit Vor- und Nachlauf, überlappende zusammengefasst */
export function detectSound(channels: Float32Array[], sr: number, c: ClipLike, o: StripOpts): [number, number][] {
  const env = envelopeMs(channels, sr, c);
  const thr = Math.pow(10, o.thresholdDb / 20);
  const runs: [number, number][] = [];
  let startF = -1;
  for (let f = 0; f <= env.length; f++) {
    const on = f < env.length && env[f] >= thr;
    if (on && startF < 0) startF = f;
    if (!on && startF >= 0) { runs.push([startF, f]); startF = -1; }
  }
  // Pausen kürzer als die Mindestdauer überbrücken
  const merged: [number, number][] = [];
  for (const r of runs) {
    const last = merged[merged.length - 1];
    if (last && r[0] - last[1] < o.minStripMs) last[1] = r[1]; else merged.push([...r] as [number, number]);
  }
  // Vor- und Nachlauf, auf den Clip begrenzt, Überlappungen zusammenfassen
  const out: [number, number][] = [];
  for (const [a, b] of merged) {
    const s = Math.max(c.start, c.start + (a - o.padStartMs) / 1000), e = Math.min(c.start + c.duration, c.start + (b + o.padEndMs) / 1000);
    const last = out[out.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e); else out.push([s, e]);
  }
  return out;
}

/** Spitze eines Clips (linear, mit Clip Gain = 0), für Normalisieren */
export function clipPeak(channels: Float32Array[], sr: number, c: Pick<Clip, 'offset' | 'duration' | 'loop'>): number {
  const len = c.loop ? Math.min(c.loop.length, c.duration) : c.duration;
  const i0 = Math.max(0, Math.floor(c.offset * sr)), i1 = Math.min(channels[0]?.length ?? 0, Math.ceil((c.offset + len) * sr));
  let pk = 0;
  for (const ch of channels) for (let i = i0; i < i1; i++) { const v = ch[i] < 0 ? -ch[i] : ch[i]; if (v > pk) pk = v; }
  return pk;
}
